import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const StatCard = ({
  icon,
  title,
  value,
  change,
  trend,
  onClick,
  subtitle,
  progressBar,
  className = "",
  size = "normal", // normal, large, small
  priority = "normal", // high, normal, low
  urgent = false,
  gradient = "blue" // blue, purple, green, orange
}) => {
  const getTrendIcon = () => {
    switch(trend) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      case 'neutral':
        return <Minus className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch(trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getCardSizing = () => {
    switch(size) {
      case 'large':
        return 'p-8 md:col-span-2';
      case 'small':
        return 'p-4';
      default:
        return 'p-6';
    }
  };

  const getValueSizing = () => {
    switch(size) {
      case 'large':
        return 'text-4xl md:text-5xl';
      case 'small':
        return 'text-2xl';
      default:
        return 'text-3xl';
    }
  };

  const getGradientClass = () => {
    switch(gradient) {
      case 'purple':
        return 'stat-card-purple';
      case 'green':
        return 'stat-card-green';
      case 'orange':
        return 'stat-card-orange';
      default:
        return 'stat-card-blue';
    }
  };

  const getIconBg = () => {
    switch(gradient) {
      case 'purple':
        return 'bg-gradient-to-br from-purple-100 to-pink-100';
      case 'green':
        return 'bg-gradient-to-br from-green-100 to-emerald-100';
      case 'orange':
        return 'bg-gradient-to-br from-orange-100 to-red-100';
      default:
        return 'bg-gradient-to-br from-blue-100 to-cyan-100';
    }
  };

  const getIconColor = () => {
    switch(gradient) {
      case 'purple':
        return 'text-purple-600';
      case 'green':
        return 'text-green-600';
      case 'orange':
        return 'text-orange-600';
      default:
        return 'text-blue-600';
    }
  };

  const getPriorityStyles = () => {
    if (urgent) {
      return 'ring-2 ring-red-300 bg-gradient-to-br from-red-50 to-pink-50 hover:shadow-lg';
    }
    return '';
  };

  const getIconSizing = () => {
    switch(size) {
      case 'large':
        return 'w-16 h-16';
      case 'small':
        return 'w-10 h-10';
      default:
        return 'w-12 h-12';
    }
  };

  return (
    <div
      className={`
        stat-card ${getGradientClass()} ${getCardSizing()} ${getPriorityStyles()}
        animate-fade-in hover-lift
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {urgent && (
        <div className="flex items-center space-x-2 mb-4 animate-pulse">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          <span className="text-xs font-semibold text-red-700 uppercase tracking-wider">
            Needs Attention
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className={`
          ${getIconSizing()} ${getIconBg()} ${getIconColor()}
          rounded-2xl flex items-center justify-center
          shadow-soft transition-transform duration-300
          hover:scale-110
        `}>
          {icon}
        </div>
        {getTrendIcon()}
      </div>

      <div className="space-y-2">
        <h3 className="text-gray-600 text-sm font-semibold uppercase tracking-wide">
          {title}
        </h3>

        <div className="flex items-baseline space-x-3">
          <p className={`
            ${getValueSizing()}
            font-bold bg-gradient-to-r from-gray-800 to-gray-600
            bg-clip-text text-transparent
          `}>
            {value}
          </p>
          {subtitle && (
            <span className="text-sm text-gray-500 font-medium">
              {subtitle}
            </span>
          )}
        </div>

        {progressBar && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-600 font-medium">Progress</span>
              <span className="text-xs text-gray-500">
                {progressBar.current} / {progressBar.target}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500 ease-out shadow-sm"
                style={{ width: `${Math.min(progressBar.percentage, 100)}%` }}
              ></div>
            </div>
          </div>
        )}

        {change && (
          <div className="flex items-center pt-2">
            <p className={`text-sm ${getTrendColor()} font-semibold flex items-center space-x-1`}>
              {trend === 'up' && <span className="text-green-500">↗</span>}
              {trend === 'down' && <span className="text-red-500">↘</span>}
              <span>{change}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatCard;
